/**
 * OrganizerRosterPanel — the whole group on one surface (§ G4), organised by
 * ORIGIN CITY (§ G10).
 *
 * The organizer's unit of work is a city, not a person: everyone out of Boston
 * shares a departure board, a fare market and a ride from the airport. A flat
 * list of eight names hides the thing they actually act on, which is "Boston is
 * sorted, Denver is not".
 *
 * Only the group's creator sees this. Attendees keep the member view (§ G5) —
 * their own trip, with the group as context, never a wall of other people's
 * itineraries.
 */
import { useState } from 'react';
import { Link } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  Users,
  PlaneLanding,
  PlaneTakeoff,
  Check,
  X,
  Plus,
  MapPin,
  FileText,
  Send,
  Search,
  Trash2,
  Pencil,
  Printer,
} from 'lucide-react';
import { travelGroupApi, type OriginGroup, type RosterMember } from '@/lib/api';
import AttendeeItineraryEditor from './AttendeeItineraryEditor';
import { useShopMeeting } from '@/hooks/useShopMeeting';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import {
  verdictTone,
  statusLabel,
  formatMoney,
  totalHeadline,
  tabLabel,
  tabNeedsAttention,
  originHeading,
  memberOriginNote,
  memberSummary,
  legShoppedNote,
  partyNote,
  anchorHeadline,
  attentionLine,
} from './organizerRosterView';

interface Props {
  groupId: string;
  /**
   * Optional: the roster response now carries the event itself, so this panel
   * works standalone on /events/:id where there is no trip page to inherit a
   * name from (§ G11). The props remain for the in-trip mount, which knows the
   * group before the roster request resolves.
   */
  groupName?: string;
  inviteCode?: string;
  /**
   * Whether to repeat the meeting time in this header. False on /events/:id,
   * where EventAnchorForm already states it directly above — saying the single
   * most important fact twice on one screen makes neither copy authoritative.
   * True on the trip page, which has no anchor panel to inherit it from.
   */
  showAnchor?: boolean;
}

function MemberRow({
  m,
  groupId,
  faresVisible,
  onResolve,
  resolving,
  onSendOptions,
  sendState,
  onRemove,
  removing,
}: {
  m: RosterMember;
  groupId: string;
  faresVisible: boolean;
  onResolve: (id: string, decision: 'confirm' | 'remove') => void;
  resolving: boolean;
  onSendOptions: (id: string) => void;
  /** null = idle. Otherwise the outcome of the last send FOR THIS PERSON. */
  sendState: { pending: boolean; message: string; ok: boolean } | null;
  onRemove: (m: RosterMember) => void;
  removing: boolean;
}) {
  const tone = verdictTone(m.anchor.verdict);
  const party = partyNote(m);
  const originNote = memberOriginNote(m);
  // § G22 — two clicks, because removing an attendee the organizer created also
  // deletes the itinerary held for them. One click next to "Send options" would
  // be a mis-tap away from destroying somebody's flights.
  const [confirming, setConfirming] = useState(false);
  // § G27 — the editor is opened per-row and closed on save, so an organizer
  // working down a roster is never editing two people at once by accident.
  const [editing, setEditing] = useState(false);

  return (
    <div className="flex flex-col gap-1 border-b border-border/60 py-2 last:border-b-0">
      <div className="flex flex-wrap items-center gap-x-2 gap-y-1">
        {/*
          The filterable/sortable flight options live on the TRIP surface
          (FlightOptionsList + FlightOptionsToolbar). Rather than rebuild that
          inside the roster, the name is the way in — one click from "Denver is
          not sorted" to actually shopping Denver's flights.
        */}
        {m.trip_id ? (
          <Link to={`/trips/${m.trip_id}`} className="text-sm font-medium text-foreground underline-offset-2 hover:underline">
            {m.display_name}
          </Link>
        ) : (
          <span className="text-sm font-medium text-foreground">{m.display_name}</span>
        )}
        {party && <span className="text-xs text-muted-foreground">({party})</span>}
        {/* § G19 — "JFK, or EWR / LGA". Their own airport stays visible even
            inside a metro tab, and so do the alternatives being shopped. */}
        {originNote && <span className="font-mono text-[11px] text-muted-foreground">{originNote}</span>}
        <span className="rounded-full border border-border px-2 py-0.5 text-[10px] uppercase tracking-wide text-muted-foreground">
          {statusLabel(m.status)}
        </span>
        <span className={`rounded-full border px-2 py-0.5 text-[10px] ${tone.className}`}>{tone.label}</span>

        <span className="ml-auto flex items-center gap-1">
          {/* § G9 — someone who put themselves on the list is confirmed by the organizer. */}
          {m.status === 'proposed' && (
            <Button
              size="sm"
              className="h-6 px-2 text-xs"
              disabled={resolving}
              onClick={() => onResolve(m.membership_id, 'confirm')}
            >
              <Check className="mr-1 h-3 w-3" /> Confirm
            </Button>
          )}

          {/* § G22 — anybody can come off the roster, not just a self-reported
              one. Until this existed the organizer's own typo was permanent. */}
          {confirming ? (
            <span className="flex items-center gap-1.5 text-xs">
              <span className="text-muted-foreground">
                {m.trip_id && !m.mcp_user_id ? 'Remove and delete their itinerary?' : 'Remove from the meeting?'}
              </span>
              <button
                className="rounded-full border border-destructive/50 px-2 py-0.5 text-[11px] text-destructive hover:bg-destructive/5 disabled:opacity-50"
                disabled={removing}
                onClick={() => {
                  setConfirming(false);
                  onRemove(m);
                }}
              >
                {removing ? 'Removing…' : 'Remove'}
              </button>
              <button
                className="text-[11px] text-muted-foreground hover:text-foreground"
                onClick={() => setConfirming(false)}
              >
                keep
              </button>
            </span>
          ) : (
            <button
              className="text-muted-foreground transition-colors hover:text-destructive"
              disabled={removing}
              onClick={() => setConfirming(true)}
              aria-label={`Remove ${m.display_name}`}
              title={`Remove ${m.display_name}`}
            >
              {m.status === 'proposed' ? <X className="h-3.5 w-3.5" /> : <Trash2 className="h-3.5 w-3.5" />}
            </button>
          )}
        </span>
      </div>

      <div className="flex flex-wrap items-center gap-x-3 pl-1 text-xs text-muted-foreground">
        <span>{memberSummary(m)}</span>
        {/* § G20 — act on "Denver is not sorted" without leaving the roster.
            Offered only where there is a trip to send; the server still refuses
            with a stated reason when there is no address or nothing shopped. */}
        {m.trip_id && (
          <button
            onClick={() => onSendOptions(m.membership_id)}
            disabled={sendState?.pending}
            className="inline-flex items-center gap-1 rounded-full border border-border px-2 py-0.5 text-[11px] transition-colors hover:bg-muted disabled:opacity-50"
          >
            <Send className="h-3 w-3" aria-hidden="true" />
            {sendState?.pending ? 'Sending…' : 'Send options'}
          </button>
        )}
        {/* § G28 — the options as PAPER. An organizer sorting eight people is
            often talking to somebody not in the app (a boss approving spend, an
            assistant booking it); a URL behind a login does not survive that
            conversation. Opens in a new tab so the roster is not lost behind a
            print dialog. */}
        {m.trip_id && !m.is_local && m.travel_mode === 'flight' && (
          <Link
            to={`/events/${groupId}/attendees/${m.membership_id}/options/print`}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-1 rounded-full border border-border px-2 py-0.5 text-[11px] transition-colors hover:bg-muted"
          >
            <Printer className="h-3 w-3" aria-hidden="true" />
            Print options
          </Link>
        )}
        {/* § G27 — the roster could add and remove an attendee and nothing in
            between, so "Alex flies Southwest out of DAL" and "put me on the
            day-before flight" were both unsayable. */}
        {m.trip_id && !m.is_local && (
          <button
            onClick={() => setEditing((v) => !v)}
            className="inline-flex items-center gap-1 rounded-full border border-border px-2 py-0.5 text-[11px] transition-colors hover:bg-muted"
          >
            <Pencil className="h-3 w-3" aria-hidden="true" />
            {editing ? 'Close' : 'Change airport or dates'}
          </button>
        )}
        {sendState && !sendState.pending && (
          <span className={sendState.ok ? 'text-muted-foreground' : 'text-destructive'}>{sendState.message}</span>
        )}
      </div>

      {editing && (
        <AttendeeItineraryEditor groupId={groupId} member={m} onClose={() => setEditing(false)} />
      )}

      {m.anchor.note && <div className="pl-1 text-xs text-muted-foreground/80">{m.anchor.note}</div>}

      <div className="flex flex-wrap gap-x-4 gap-y-1 pl-1">
        {m.legs.map((l, i) => {
          const Icon = l.leg_role === 'return' ? PlaneTakeoff : PlaneLanding;
          const money = formatMoney(l.fare);
          const shopped = legShoppedNote(l);
          return (
            <span key={i} className="inline-flex items-center gap-1.5 text-xs text-muted-foreground">
              <Icon className="h-3.5 w-3.5 shrink-0" aria-hidden="true" />
              {l.label ?? 'no flight chosen'}
              {/* Only a recorded booking price appears here. No fare is not $0. */}
              {faresVisible && money && (
                <span className="font-medium text-foreground">
                  {money}
                  {l.price_basis === 'total' ? ' total' : ' pp'}
                </span>
              )}
              {/* § G23 — what shopping FOUND, shown only where nothing is booked
                  so an estimate never sits where money paid belongs. Without it
                  a sweep that stored 451 options rendered as a blank row. */}
              {faresVisible && !money && shopped && (
                <span className="text-muted-foreground/90">{shopped}</span>
              )}
              {l.booked && <span className="text-[10px] uppercase tracking-wide">booked</span>}
            </span>
          );
        })}
      </div>
    </div>
  );
}

function AddAttendeeForm({ groupId, onDone }: { groupId: string; onDone: () => void }) {
  const [name, setName] = useState('');
  const [airport, setAirport] = useState('');
  const [alts, setAlts] = useState('');
  const [email, setEmail] = useState('');
  const [error, setError] = useState('');

  const mut = useMutation({
    mutationFn: () =>
      travelGroupApi.addAttendee(groupId, {
        display_name: name.trim(),
        home_airport: airport.trim().toUpperCase(),
        email: email.trim() || null,
        // § G19 — "EWR, LGA" or "EWR LGA" or "EWR/LGA"; an organizer typing fast
        // should not have to learn a separator.
        alternate_airports: alts
          .split(/[\s,/]+/)
          .map((a) => a.trim().toUpperCase())
          .filter(Boolean),
      }),
    onSuccess: () => {
      setName('');
      setAirport('');
      setAlts('');
      setEmail('');
      setError('');
      onDone();
    },
    onError: (e: any) => setError(e?.response?.data?.error || e.message),
  });

  return (
    <form
      className="flex flex-wrap items-center gap-2 pt-2"
      onSubmit={(e) => {
        e.preventDefault();
        if (name.trim() && airport.trim()) mut.mutate();
      }}
    >
      <Input placeholder="Name" value={name} onChange={(e) => setName(e.target.value)} className="h-7 w-36 text-xs" />
      <Input
        placeholder="Home airport"
        value={airport}
        onChange={(e) => setAirport(e.target.value.toUpperCase())}
        className="h-7 w-28 font-mono text-xs"
      />
      <Input
        placeholder="Also try (optional)"
        title="Other airports this person could fly from, e.g. EWR LGA"
        value={alts}
        onChange={(e) => setAlts(e.target.value.toUpperCase())}
        className="h-7 w-32 font-mono text-xs"
      />
      <Input
        placeholder="Email (optional)"
        value={email}
        onChange={(e) => setEmail(e.target.value)}
        className="h-7 w-44 text-xs"
      />
      <Button type="submit" size="sm" className="h-7 text-xs" disabled={mut.isPending || !name.trim() || !airport.trim()}>
        {mut.isPending ? 'Adding…' : 'Add'}
      </Button>
      {/* They do not need a TripMuscle account — that is the point of § G2. */}
      <span className="text-xs text-muted-foreground/70">No account needed</span>
      {error && <span className="text-xs text-destructive">{error}</span>}
    </form>
  );
}

export default function OrganizerRosterPanel({ groupId, groupName, inviteCode, showAnchor = true }: Props) {
  const qc = useQueryClient();
  const [activeOrigin, setActiveOrigin] = useState<string | null>(null);
  const [adding, setAdding] = useState(false);

  const { data: roster, isLoading } = useQuery({
    queryKey: ['travel-group-roster', groupId],
    // A non-organizer gets a 403 here by design; the member view is what they see.
    queryFn: () => travelGroupApi.roster(groupId).catch(() => null),
    retry: false,
  });

  const resolveMut = useMutation({
    mutationFn: ({ id, decision }: { id: string; decision: 'confirm' | 'remove' }) =>
      travelGroupApi.resolveAttendee(groupId, id, decision),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['travel-group-roster', groupId] }),
  });

  /**
   * § G22 — removal. The server decides whether the itinerary goes with the
   * person (organizer's own unclaimed trip) or stays (it is theirs), and returns
   * a sentence saying which; that sentence is shown rather than a generic
   * "removed", because "did I just delete their flights?" is the only question an
   * organizer has at that moment.
   */
  const [removeNote, setRemoveNote] = useState('');
  const removeMut = useMutation({
    mutationFn: (membershipId: string) => travelGroupApi.removeAttendee(groupId, membershipId),
    onSuccess: (res) => {
      setRemoveNote(res.note);
      qc.invalidateQueries({ queryKey: ['travel-group-roster', groupId] });
      // Their trip may have just been deleted — the dashboard is showing it.
      qc.invalidateQueries({ queryKey: ['trips'] });
    },
    onError: (e: any) => setRemoveNote(e?.response?.data?.error || e.message),
  });

  // § G23 — shop everyone's fares now instead of waiting for the 6-hourly sweep.
  const shop = useShopMeeting(groupId, () => {
    qc.invalidateQueries({ queryKey: ['travel-group-roster', groupId] });
  });

  /**
   * § G20 — keyed BY MEMBERSHIP, not a single shared flag. The organizer's job
   * here is chasing several people in a row, and one global "sending…" would
   * light up every row and then report one person's refusal against everybody.
   */
  const [sendState, setSendState] = useState<
    Record<string, { pending: boolean; message: string; ok: boolean }>
  >({});

  function sendOptions(membershipId: string) {
    setSendState((s) => ({ ...s, [membershipId]: { pending: true, message: '', ok: true } }));
    travelGroupApi
      .sendFlightOptions(groupId, membershipId)
      .then((r) =>
        setSendState((s) => ({
          ...s,
          [membershipId]: {
            pending: false,
            // A composed email that SMTP never sent is not a success. Saying
            // "sent" here would leave the organizer believing somebody had been
            // told when nothing left the building.
            ok: r.sent,
            message: r.sent
              ? `Sent ${r.options} options to ${r.to}`
              : `Not delivered — email is not configured on this server`,
          },
        }))
      )
      .catch((e: any) =>
        setSendState((s) => ({
          ...s,
          [membershipId]: {
            pending: false,
            ok: false,
            message: e?.response?.data?.error || e.message,
          },
        }))
      );
  }

  if (isLoading || !roster) return null;

  // Prefer the server's own copy — on /events/:id there are no props to fall
  // back to, and on the trip page the two agree.
  const name = roster.event?.name ?? groupName ?? 'Group';
  const code = roster.event?.invite_code ?? inviteCode ?? '';
  const origins = roster.origins;
  const active: OriginGroup | undefined =
    origins.find((o) => o.origin === activeOrigin) ?? origins[0];
  const attention = attentionLine(roster.coverage);

  return (
    <section className="rounded-lg border border-border bg-background p-3">
      <header className="flex flex-wrap items-baseline gap-x-3 gap-y-1">
        <span className="flex items-center gap-1.5 text-sm font-medium">
          <Users className="h-4 w-4" />
          {name}
        </span>
        {showAnchor && (
          <span className="text-xs text-muted-foreground">{anchorHeadline(roster.anchor)}</span>
        )}
        <span className="ml-auto flex items-center gap-3 text-xs text-muted-foreground">
          <span>
            {roster.coverage.label} · {roster.coverage.travellers} travelling
          </span>
          {/*
            § G23 — the button that was missing. An organizer could add eight
            people and then had nothing to press: the roster said "8 with no
            flights" and the only thing that would ever change it was the
            6-hourly cron. Adding an attendee now starts their own check too, so
            this is for re-shopping and for the eight who predate that.
          */}
          <button
            onClick={shop.start}
            disabled={shop.phase === 'shopping'}
            className="inline-flex items-center gap-1 rounded-full border border-border px-2.5 py-0.5 transition-colors hover:bg-muted disabled:opacity-60"
            title="Check fares for every attendee with an itinerary"
          >
            <Search className="h-3 w-3" aria-hidden="true" />
            {shop.phase === 'shopping' ? 'Shopping…' : 'Shop fares'}
          </button>
          {/* § G16 — the shareable artifact, one click from the working surface. */}
          <Link
            to={`/events/${groupId}/manifest`}
            className="inline-flex items-center gap-1 rounded-full border border-border px-2.5 py-0.5 hover:bg-muted"
          >
            <FileText className="h-3 w-3" /> Manifest
          </Link>
        </span>
      </header>

      <div className="mt-1 flex flex-wrap items-baseline gap-x-3 text-xs">
        <span className="font-medium text-foreground">{totalHeadline(roster.total, roster.fares_visible, roster.fare_headline)}</span>
        {attention && <span className="text-muted-foreground">{attention}</span>}
      </div>

      {/* Progress and outcome, in the server's own words ("3 of 8 — shopping
          Darin…"). A spinner with no name attached is indistinguishable from a
          wedged scrape. */}
      {shop.message && (
        <p
          className={`mt-1 text-xs ${shop.phase === 'error' ? 'text-destructive' : 'text-muted-foreground'}`}
          role="status"
        >
          {shop.message}
        </p>
      )}
      {removeNote && <p className="mt-1 text-xs text-muted-foreground">{removeNote}</p>}

      {/* § G10 — a tab per origin city, biggest first, stable order. */}
      {origins.length > 0 && (
        <div className="mt-3 flex flex-wrap gap-1 border-b border-border pb-2" role="tablist">
          {origins.map((o) => {
            const isActive = o.origin === active?.origin;
            return (
              <button
                key={o.origin}
                role="tab"
                aria-selected={isActive}
                onClick={() => setActiveOrigin(o.origin)}
                className={`rounded-full border px-2.5 py-0.5 text-xs transition-colors ${
                  isActive ? 'border-foreground bg-foreground text-background' : 'border-border text-muted-foreground hover:bg-muted'
                } ${tabNeedsAttention(o) && !isActive ? 'border-destructive/50' : ''}`}
              >
                <MapPin className="mr-1 inline h-3 w-3" aria-hidden="true" />
                {tabLabel(o)}
                {tabNeedsAttention(o) && (
                  <span
                    className="ml-1 text-destructive"
                    aria-label="needs attention"
                    title="Someone here still needs attention"
                  >
                    •
                  </span>
                )}
              </button>
            );
          })}
        </div>
      )}

      {active && (
        <div className="mt-2">
          <div className="flex items-baseline justify-between">
            <span className="text-xs uppercase tracking-wide text-muted-foreground">
              {originHeading(active)}
            </span>
            {roster.fares_visible && active.total && (
              <span className="text-xs text-muted-foreground">
                {totalHeadline(active.total, roster.fares_visible, active.fare_headline)}
              </span>
            )}
          </div>
          {active.members.map((m) => (
            <MemberRow
              key={m.membership_id}
              m={m}
              groupId={groupId}
              faresVisible={roster.fares_visible}
              resolving={resolveMut.isPending}
              onResolve={(id, decision) => resolveMut.mutate({ id, decision })}
              onSendOptions={sendOptions}
              sendState={sendState[m.membership_id] ?? null}
              onRemove={(member) => {
                setRemoveNote('');
                removeMut.mutate(member.membership_id);
              }}
              removing={removeMut.isPending && removeMut.variables === m.membership_id}
            />
          ))}
        </div>
      )}

      {origins.length === 0 && (
        <p className="mt-3 text-xs text-muted-foreground">
          Nobody on the roster yet. Add attendees below — they do not need an account.
        </p>
      )}

      <footer className="mt-2 border-t border-border pt-2">
        {adding ? (
          <AddAttendeeForm
            groupId={groupId}
            onDone={() => {
              setAdding(false);
              qc.invalidateQueries({ queryKey: ['travel-group-roster', groupId] });
            }}
          />
        ) : (
          <div className="flex flex-wrap items-center gap-3">
            <button
              onClick={() => setAdding(true)}
              className="inline-flex items-center gap-1 rounded-full border border-dashed border-border px-2.5 py-0.5 text-xs text-muted-foreground transition-colors hover:bg-muted"
            >
              <Plus className="h-3 w-3" /> Add attendee
            </button>
            {code && (
              <span className="text-xs text-muted-foreground/70">
                or share invite code <span className="font-mono font-bold tracking-widest">{code}</span>
              </span>
            )}
          </div>
        )}
      </footer>
    </section>
  );
}
